/**
 * Dynamic theme class helpers — pick light/dark Tailwind classes from a single map.
 *
 * @example
 * themeClass(isDark, { light: 'bg-white text-gray-900', dark: 'bg-sparepe-card text-sparepe-ink' })
 */

export function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}

/**
 * @param {boolean} isDark
 * @param {{ light?: string, dark?: string }} map
 * @param {string} [extra]
 */
export function themeClass(isDark, map, extra = '') {
  const base = isDark ? map.dark : map.light;
  return cn(base, extra);
}

/** Semantic storefront / page surfaces */
export const themeSemantic = {
  pageShell: {
    light: 'sp-storefront sp-storefront--light min-h-screen bg-[var(--sp-bg-deep)] text-[var(--sp-ink)]',
    dark: 'home-page-dark sp-page-dark sp-storefront min-h-screen bg-[var(--sp-bg-deep)] text-[var(--sp-ink)]',
  },
  pageBg: {
    light: 'bg-slate-50 text-slate-900',
    dark: 'bg-sparepe-deep text-sparepe-ink',
  },
  surface: {
    light: 'bg-white text-slate-900 border-slate-200',
    dark: 'bg-sparepe-surface text-sparepe-ink border-sparepe-border',
  },
  card: {
    light: 'bg-white text-slate-900 border border-slate-200 shadow-sm',
    dark: 'bg-sparepe-card text-sparepe-ink border border-sparepe-border-md shadow-sp-premium',
  },
  textPrimary: {
    light: 'text-slate-900',
    dark: 'text-sparepe-ink',
  },
  textSecondary: {
    light: 'text-slate-600',
    dark: 'text-sparepe-ink-2',
  },
  textMuted: {
    light: 'text-slate-500',
    dark: 'text-sparepe-ink-3',
  },
  textAccent: {
    light: 'text-amber-700',
    dark: 'text-sparepe-accent-dk',
  },
  link: {
    light: 'text-primary-600 hover:text-primary-700',
    dark: 'text-sparepe-accent hover:text-sparepe-accent-dk',
  },
  input: {
    light: 'bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500/30',
    dark: 'bg-sparepe-surface text-sparepe-ink border-sparepe-border-md placeholder:text-sparepe-ink-3 focus:border-sparepe-accent focus:ring-sparepe-accent/20',
  },
  headerChrome: {
    light: 'sp-storefront-chrome sp-storefront-chrome--light border-b border-slate-200 bg-white/95 text-slate-900',
    dark: 'sp-storefront-chrome dark border-b border-sparepe-border',
  },
  bottomNav: {
    light: 'border-t border-gray-200 bg-white/95 text-gray-800 shadow-[0_-4px_24px_rgba(0,0,0,0.08)]',
    dark: 'sp-bottom-nav text-sparepe-ink',
  },
  iconBtn: {
    light: 'p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors',
    dark: 'sparepe-header__icon-btn sparepe-header__iBtn',
  },
  sectionLabel: {
    light: 'text-xs font-semibold uppercase tracking-wider text-slate-500',
    dark: 'sp-section-label',
  },
  border: {
    light: 'border-slate-200',
    dark: 'border-sparepe-border',
  },
  footerShell: {
    light: 'border-t border-gray-200 bg-white text-gray-700 text-sm relative',
    dark: 'sp-footer border-t border-sparepe-border text-sm relative bg-sparepe-surface',
  },
  footerLink: {
    light: 'text-xs sm:text-sm md:text-base text-slate-600 hover:text-slate-900 transition-colors block',
    dark: 'text-xs sm:text-sm md:text-base text-gray-400 hover:text-white transition-colors block',
  },
  footerBlurb: {
    light: 'mt-3 sm:mt-4 text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed',
    dark: 'mt-3 sm:mt-4 text-gray-400 text-xs sm:text-sm md:text-base leading-relaxed',
  },
  footerSocialBar: {
    light: 'bg-slate-900 border-t border-slate-200',
    dark: 'bg-gray-800 border-t border-gray-700',
  },
  footerCopyrightBar: {
    light: 'bg-white border-t border-slate-200',
    dark: 'bg-sparepe-surface border-t border-sparepe-border',
  },
  footerCopyrightText: {
    light: 'text-slate-500 text-xs sm:text-sm text-center md:text-left',
    dark: 'text-gray-400 text-xs sm:text-sm text-center md:text-left',
  },
  catalogMain: {
    light: 'catalog-page min-h-screen bg-slate-50 text-slate-900 py-6 sm:py-8 md:py-10 px-4 sm:px-6 lg:px-8',
    dark: 'catalog-page min-h-screen bg-slate-950 text-slate-200 py-6 sm:py-8 md:py-10 px-4 sm:px-6 lg:px-8',
  },
  pdpShell: {
    light: 'pdp-page min-h-screen bg-slate-50 text-slate-900',
    dark: 'pdp-page min-h-screen bg-[#0b1120] text-slate-100',
  },
  pdpPanel: {
    light: 'rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm',
    dark: 'rounded-xl border border-slate-700/60 bg-slate-800/30 p-4 sm:p-5',
  },
  pdpPanelTitle: {
    light: 'mb-4 text-base font-semibold text-slate-900',
    dark: 'mb-4 text-base font-semibold text-slate-200',
  },
  pdpMiniCard: {
    light: 'group rounded-xl border border-slate-200 bg-slate-50 p-2 transition hover:border-amber-400 hover:shadow-sm',
    dark: 'group rounded-xl border border-slate-700/60 bg-slate-800/50 p-2 transition hover:border-blue-500/50',
  },
  pdpMiniCardImage: {
    light: 'flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-slate-100 bg-white',
    dark: 'flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-slate-900',
  },
  pdpInput: {
    light: 'flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20',
    dark: 'flex-1 rounded-xl border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none',
  },
  pdpThreadItem: {
    light: 'rounded-xl border border-slate-200 bg-slate-50 p-3',
    dark: 'rounded-xl border border-slate-700/40 bg-slate-900/50 p-3',
  },
  pdpAccordion: {
    light: 'overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm',
    dark: 'overflow-hidden rounded-xl border border-slate-700/60 bg-slate-800/30',
  },
  /* Flipkart-style: yellow Add to Cart + orange Buy Now */
  pdpAddToCartBtn: {
    light:
      'flex flex-1 items-center justify-center gap-2 rounded-sm bg-[#ff9f00] px-4 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition hover:bg-[#f39000] disabled:opacity-50 min-h-[3rem] sm:text-[0.9375rem]',
    dark:
      'flex flex-1 items-center justify-center gap-2 rounded-sm bg-[#ff9f00] px-4 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition hover:bg-[#f39000] disabled:opacity-50 min-h-[3rem] sm:text-[0.9375rem]',
  },
  pdpBuyNowBtn: {
    light:
      'flex min-w-0 flex-1 items-center justify-center gap-2 rounded-sm bg-[#fb641b] px-4 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition hover:bg-[#e55a16] min-h-[3rem] sm:text-[0.9375rem]',
    dark:
      'flex min-w-0 flex-1 items-center justify-center gap-2 rounded-sm bg-[#fb641b] px-4 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition hover:bg-[#e55a16] min-h-[3rem] sm:text-[0.9375rem]',
  },
  brandsShell: {
    light: 'brands-page min-h-screen bg-slate-50 text-slate-900',
    dark: 'brands-page min-h-screen bg-[#0a0d1a] text-slate-100',
  },
  staticPageShell: {
    light: 'static-page min-h-screen bg-gray-50 text-gray-900',
    dark: 'static-page min-h-screen bg-transparent text-inherit',
  },
  catalogToolbar: {
    light: 'mb-6 scroll-mt-28 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-md',
    dark: 'mb-6 scroll-mt-28 rounded-2xl border border-slate-700/90 bg-slate-900/70 p-4 sm:p-5 shadow-lg shadow-black/25',
  },
  catalogSearchInput: {
    light: 'block w-full pl-10 pr-3 py-3 rounded-xl border border-slate-300 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400',
    dark: 'block w-full pl-10 pr-3 py-3 rounded-xl border border-slate-600/90 bg-slate-950/80 text-sm text-white placeholder:text-slate-500 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/60',
  },
  catalogSelect: {
    light: 'w-full min-w-0 px-3 py-3 rounded-xl border border-slate-300 bg-white text-sm text-slate-900 focus:ring-2 focus:ring-amber-500/40 cursor-pointer',
    dark: 'w-full min-w-0 px-3 py-3 rounded-xl border border-slate-600 bg-slate-950/80 text-sm text-slate-200 focus:ring-2 focus:ring-amber-500/50 cursor-pointer',
  },
  catalogFilterBtn: {
    light: 'inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-slate-700 text-sm font-semibold hover:bg-slate-100 transition-colors',
    dark: 'inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-600 bg-slate-800/90 text-slate-200 text-sm font-semibold hover:bg-slate-700/90 transition-colors',
  },
  catalogChip: {
    light: 'rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-400 hover:text-slate-800 transition-colors',
    dark: 'rounded-full border border-slate-600/80 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-400 hover:border-slate-500 hover:text-slate-300 transition-colors',
  },
  catalogActiveChip: {
    light: 'inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800',
    dark: 'inline-flex items-center gap-1.5 rounded-full border border-amber-500/70 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 uppercase tracking-wide',
  },
  catalogVehicleStrip: {
    light: 'bg-white border border-slate-200 rounded-2xl p-4 mb-8 shadow-sm',
    dark: 'bg-slate-900/90 border border-slate-700/90 rounded-2xl p-4 mb-8 shadow-lg shadow-black/20',
  },
  catalogTierIdle: {
    light: 'text-left p-5 rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 shadow-sm transition-all',
    dark: 'text-left p-5 rounded-2xl border border-slate-700 bg-slate-900/60 hover:border-slate-600 hover:bg-slate-900 transition-all',
  },
  catalogTierActive: {
    light: 'text-left p-5 rounded-2xl border border-amber-400 bg-amber-50 ring-2 ring-amber-400/30 shadow-md transition-all',
    dark: 'text-left p-5 rounded-2xl border border-amber-500/80 bg-slate-900/90 ring-2 ring-amber-500/30 shadow-lg shadow-amber-900/10 transition-all',
  },
  catalogPanel: {
    light: 'rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm',
    dark: 'rounded-2xl border border-slate-700/90 bg-slate-900/50 overflow-hidden shadow-xl shadow-black/30',
  },
  searchResultsPanel: {
    light: 'mb-8 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-md',
    dark: 'mb-8 rounded-2xl border border-slate-700/90 bg-slate-900/80 p-4 sm:p-5 shadow-lg',
  },
  searchResultsCard: {
    light: 'rounded-xl border border-slate-200 bg-slate-50 p-4 hover:border-amber-400 hover:shadow-sm transition-colors',
    dark: 'rounded-xl border border-slate-700 bg-slate-950/60 p-4 hover:border-amber-500/50 transition-colors',
  },
  searchResultsExpressToggle: {
    light: 'inline-flex items-center gap-2 cursor-pointer rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5',
    dark: 'inline-flex items-center gap-2 cursor-pointer rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2.5',
  },
  seoSection: {
    light: 'rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 md:p-8 shadow-sm',
    dark: 'rounded-2xl border border-slate-700/90 bg-slate-900/60 p-5 sm:p-6 md:p-8',
  },
  secondaryBtn: {
    light:
      'px-5 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:border-amber-400 hover:text-amber-700 transition-colors shadow-sm',
    dark:
      'px-5 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-200 text-sm font-semibold hover:border-amber-500/50 hover:text-amber-200 transition-colors',
  },
  comboCard: {
    light: 'rounded-2xl border border-slate-200 bg-white p-4 hover:border-amber-300 shadow-sm transition-colors',
    dark: 'rounded-2xl border border-slate-700 bg-[#121826]/90 p-4 hover:border-slate-500 transition-colors',
  },
  couponCard: {
    light: 'relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm',
    dark: 'relative overflow-hidden rounded-2xl border border-slate-700 bg-[#131a2a] p-4',
  },
};

/**
 * @param {boolean} isDark
 * @param {keyof typeof themeSemantic} key
 * @param {string} [extra]
 */
export function semanticClass(isDark, key, extra = '') {
  return themeClass(isDark, themeSemantic[key], extra);
}

export function storefrontShellClass(isDark) {
  return semanticClass(isDark, 'pageShell');
}
